import { z } from "zod";
import { Currencies } from "./enums";
import { apiListSchema } from "./list";

//? dto
export const CreatePaymentDtoSchema = z.object({
  ReservationId: z.number().nullish(),
  Value: z.number().nullish(),
  State: z.number().nullish(),
  IsDeposit: z.boolean(),
  PaymentMethod: z.number().nullish(),
  Currency: z.number().nullish(),
  PaymentDate: z.string().nullish(),
  Invoice: z.string().nullish(),
  InvoiceUrl: z.string().nullish(),
  Notes: z.string().nullish(),
  Payee: z.string(),
  Balance: z.number().nullish(),
});
export type CreatePaymentDto = z.infer<typeof CreatePaymentDtoSchema>;

export const PaymentDtoSchema = CreatePaymentDtoSchema.extend({
  Id: z.number(),
});
export type PaymentDto = z.infer<typeof PaymentDtoSchema>;

export const paymentDtoListSchema = apiListSchema(PaymentDtoSchema);
//? model
export enum PaymentState {
  Payed = "Payed",
  Sent = "Sent",
  No = "No",
}

export const paymentStateAsArray = Object.values(PaymentState);

export const paymentStateEnumToNumber: Record<PaymentState, number> = {
  Payed: 0,
  Sent: 1,
  No: 2,
};

const paymentStateNumToEnum = Object.entries(paymentStateEnumToNumber).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {},
) as Record<number, PaymentState>;

export enum PaymentMethod {
  Cash = "Cash",
  CreditCard = "CreditCard",
  BankTransfer = "BankTransfer",
  Check = "Check",
  Paypal = "Paypal",
  Other = "Other",
}

export const paymentMethodAsArray = Object.values(PaymentMethod);

export const paymentMethodEnumToNumber: Record<PaymentMethod, number> = {
  Cash: 0,
  CreditCard: 1,
  BankTransfer: 2,
  Check: 3,
  Paypal: 4,
  Other: 5,
};

const paymentMethodNumToEnum = Object.entries(paymentMethodEnumToNumber).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {},
) as Record<number, PaymentMethod>;

export const CreatePaymentSchema = z.object({
  reservationId: z.number().nullish(),
  value: z.number().nullish(),
  state: z.nativeEnum(PaymentState).nullish(),
  isDeposit: z.boolean(),
  paymentMethod: z.nativeEnum(PaymentMethod).nullish(),
  currency: z.string().nullish(),
  paymentDate: z.date().nullish(),
  invoice: z.string().nullish(),
  invoiceUrl: z.string().nullish(),
  notes: z.string().nullish(),
  payee: z.string(),
  balance: z.number().nullish(),
});

export type CreatePayment = z.infer<typeof CreatePaymentSchema>;
export const PaymentSchema = CreatePaymentSchema.extend({
  id: z.string(),
});
export type Payment = z.infer<typeof PaymentSchema>;

//? mappers

export function paymentFromDto(dto: PaymentDto): Payment {
  return {
    id: dto.Id.toString(),
    reservationId: dto.ReservationId,
    value: dto.Value,
    state:
      typeof dto.State === "number"
        ? paymentStateNumToEnum[dto.State]
        : undefined,
    isDeposit: dto.IsDeposit,
    paymentMethod:
      typeof dto.PaymentMethod === "number"
        ? paymentMethodNumToEnum[dto.PaymentMethod]
        : undefined,
    currency: Currencies[dto.Currency ?? -1],
    paymentDate: dto.PaymentDate ? new Date(dto.PaymentDate) : undefined,
    invoice: dto.Invoice,
    invoiceUrl: dto.InvoiceUrl,
    notes: dto.Notes,
    payee: dto.Payee,
    balance: dto.Balance,
  };
}
export function paymentToDto(model: Payment): PaymentDto {
  return {
    Id: Number.parseInt(model.id),
    ...createPaymentToDto(model),
  };
}
export function createPaymentToDto(model: CreatePayment): CreatePaymentDto {
  return {
    ReservationId: model.reservationId,
    Value: model.value,
    State: model.state ? paymentStateEnumToNumber[model.state] : undefined,
    IsDeposit: model.isDeposit,
    PaymentMethod: model.paymentMethod
      ? paymentMethodEnumToNumber[model.paymentMethod]
      : undefined,
    Currency: model.currency ? Currencies.indexOf(model.currency) : undefined,
    PaymentDate: model.paymentDate?.toDateString(),
    Invoice: model.invoice,
    InvoiceUrl: model.invoiceUrl,
    Notes: model.notes,
    Payee: model.payee,
    Balance: model.balance,
  };
}
